const inFlight = new Map();

// Shared by creation, navigation, and saved-message callbacks. Return the same
// promise to concurrent callers instead of installing duplicate listeners.
export function requestConversationTitle({ userId, conversationId, getChat, getSocket, isPlaceholder, onTitle, maxRetries = 3 }) {
  const key = `${userId}:${conversationId}`;
  if (inFlight.has(key)) return inFlight.get(key);
  const run = async () => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const chat = getChat();
      if (!chat) return false;
      if (chat.title && !isPlaceholder(chat.title)) return true;
      if (!chat.messages?.some(message => message.role === 'user' && !message.isPending
        && typeof message.content === 'string' && message.content.trim())) return false;
      const client = getSocket();
      const socket = client?.socket;
      if (!socket) return false;
      const result = await new Promise(resolve => {
        let settled = false;
        let timer;
        const finish = value => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          socket.off('conversation_title', listener);
          resolve(value);
        };
        const receive = title => {
          if (settled || !title || isPlaceholder(title)) return;
          if (!getChat()) return finish('stop');
          onTitle(title);
          finish('ready');
        };
        const listener = data => {
          if ((data.id || data.conversationId) === conversationId) receive(data.title);
        };
        socket.on('conversation_title', listener);
        timer = setTimeout(() => finish('retry'), 45000);
        try {
          const emitted = client.emitRetrieveConversationTitle(userId, conversationId, response => {
            if (response?.status === 'not_ready') finish('stop');
            else if (response?.status === 'ready') receive(response.title);
            else if (response?.status === 'error') finish('retry');
          });
          if (emitted === false) finish('stop');
        } catch (_) {
          finish('retry');
        }
      });
      if (result === 'ready') return true;
      if (result === 'stop') return false;
      if (attempt + 1 < maxRetries) await new Promise(resolve => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 5000)));
    }
    return false;
  };
  const promise = Promise.resolve().then(run).finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
}
